import { Tooltip } from "react-tooltip";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { ChevronRightIcon, ClockIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { localeDate } from "../../../app/datetimeUtils";
import { getStepName } from "../../../app/draftHelper";
import Avatar from "../../../components/Avatar";
import { DraftStatus } from "../../writing/writingApiSlice";
import {
	PeerReviewListItemStatus,
	PeerReviewListItemViewModel,
} from "../../peerReviews/peerReviewsApiSlice";
import { WriterHomeSpaLessonsViewModel, WriterHomeSpaWritingViewModel } from "../homeApiSlice";

interface StatusSectionProps {
	isWritingCard: boolean;
	isReviewsCard: boolean;
	isLessonsCard: boolean;
	essay?: WriterHomeSpaWritingViewModel;
	review?: PeerReviewListItemViewModel;
	lesson?: WriterHomeSpaLessonsViewModel;
}

export default function StatusSection(props: StatusSectionProps) {
	return (
		<div className="flex justify-between items-center">
			{props.isWritingCard && props.essay && (
				<div className="flex gap-2 items-center">
					<StatusIcon notStarted={props.essay.status === DraftStatus.NotStarted} />
					<div>
						{props.essay.status === DraftStatus.NotStarted ? (
							<NotStarted />
						) : (
							<div className="text-sm text-gray-700 space-y-2">
								<div className="mt-1 items-center">
									<p>
										Last updated: {localeDate(props.essay.lastUpdateDate ?? "")}
									</p>
									<p>{getStepName(props.essay.step, props.essay.action)}</p>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			{props.isReviewsCard && props.review && (
				<div className="flex gap-2 items-center">
					<StatusIcon
						notStarted={props.review?.status === PeerReviewListItemStatus.NotStarted}
						complete={props.review?.status === PeerReviewListItemStatus.Complete}
					/>
					{props.review.status === PeerReviewListItemStatus.Complete ? (
						<ReviewStatus request={false} review={props.review} />
					) : (
						<ReviewStatus request={true} review={props.review} />
					)}
				</div>
			)}
			{props.isLessonsCard && props.lesson && (
				<div className="flex gap-2 items-center">
					<StatusIcon notStarted={!props.lesson.timeStarted} />
					<div>
						{!props.lesson.timeStarted ? (
							<NotStarted />
						) : (
							<div className="text-sm text-gray-700 space-y-2">
								<div className="mt-1 items-center">
									<p>Started on: {localeDate(props.lesson.timeStarted ?? "")}</p>
									<p>
										Time Spent:{" "}
										<span>
											{Math.round(props.lesson.timeSpentSeconds ?? 0 / 60)}{" "}
											min
										</span>
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			<ChevronRightIcon className="size-7 text-primary-700 ml-2" />
		</div>
	);
}

interface StatusIconProps {
	notStarted?: boolean;
	complete?: boolean;
}

function StatusIcon(props: StatusIconProps) {
	return (
		<div>
			{props.notStarted ? (
				<ExclamationTriangleIcon
					tabIndex={-1}
					className="size-8 text-yellow-500"
					aria-hidden="true"
					data-tooltip-id="not-started-tooltip"
				/>
			) : props.complete ? (
				<CheckBadgeIcon
					tabIndex={-1}
					className="size-8 text-green-700"
					aria-hidden="true"
					data-tooltip-id="completed-tooltip"
				/>
			) : (
				<ClockIcon
					tabIndex={-1}
					className="size-8 text-green-700"
					aria-hidden="true"
					data-tooltip-id="in-progress-tooltip"
				/>
			)}
			<Tooltip id="not-started-tooltip" place="top" content="Not Started" />
			<Tooltip id="in-progress-tooltip" place="top" content="In Progress" />
			<Tooltip id="completed-tooltip" place="top" content="Completed" />
		</div>
	);
}

function NotStarted() {
	return (
		<p className="text-sm text-gray-700">
			You have <span className="font-semibold">not started</span> yet.
		</p>
	);
}

interface ReviewStatusProps {
	request: boolean;
	review: PeerReviewListItemViewModel;
}

function ReviewStatus(props: ReviewStatusProps) {
	const user = props.request ? props.review.author : props.review.reviewer;
	return (
		<div className="text-sm text-gray-700">
			<p>
				Review{" "}
				<span className="font-semibold">{props.request ? "requested" : "completed"}</span>{" "}
				{props.request ? "from" : "by"}:
			</p>
			<div className="flex mt-1 items-center">
				<Avatar
					hasImage={user.hasImage}
					initials={user.initials ?? "?"}
					userId={user.userId}
					className="ml-1 mr-2"
					mini={true}
				/>
				<div>
					<p>{user.name}</p>
					<p>
						on{" "}
						{localeDate(
							(props.request ? props.review.requestDate : props.review.reviewDate) ??
								""
						)}
					</p>
				</div>
			</div>
		</div>
	);
}
